import React from 'react';
import PropTypes from 'prop-types';
import './Divider.css';

/**
 * Divider component - A reusable and easily customizable divider component
 *
 * @param {Object} props - Component props
 * @param {string} props.type - Divider type: 'horizontal' | 'vertical'
 * @param {number} props.thickness - Divider thickness (px)
 * @param {string} props.color - Divider color
 * @param {string|number} props.margin - Margin spacing
 * @param {string|number} props.width - Width (for horizontal) or height (for vertical)
 * @param {boolean} props.dashed - Display as dashed line
 * @param {boolean} props.dotted - Display as dotted line
 * @param {Object} props.style - Custom style object
 * @param {string} props.className - CSS class name
 * @param {React.ReactNode} props.children - Text content or icon in the middle of divider
 */
const Divider = ({
  type = 'horizontal',
  thickness = 1,
  color = '#E1E3E5',
  margin = '10px 0',
  width = '100%',
  dashed = false,
  dotted = false,
  style = {},
  className = '',
  children,
  ...props
}) => {
  const getBaseStyle = () => {
    const borderStyle = dashed ? 'dashed' : dotted ? 'dotted' : 'solid';

    if (type === 'vertical') {
      return {
        width: thickness,
        height: width,
        borderLeft: `${thickness}px ${borderStyle} ${color}`,
        margin: typeof margin === 'string' ? margin : `0 ${margin}px`,
        display: 'inline-block'
      };
    }

    return {
      height: thickness,
      width: width,
      backgroundColor: !dashed && !dotted ? color : 'transparent',
      borderTop: dashed || dotted ? `${thickness}px ${borderStyle} ${color}` : 'none',
      margin: typeof margin === 'string' ? margin : `${margin}px 0`
    };
  };

  const baseStyle = getBaseStyle();
  const finalStyle = {...baseStyle, ...style};

  if (children) {
    return (
      <div
        className={`divider-with-content ${className}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          margin: typeof margin === 'string' ? margin : `${margin}px 0`,
          ...style
        }}
        {...props}
      >
        <div
          style={{
            flex: 1,
            height: thickness,
            backgroundColor: !dashed && !dotted ? color : 'transparent',
            borderTop:
              dashed || dotted ? `${thickness}px ${dashed ? 'dashed' : 'dotted'} ${color}` : 'none'
          }}
        />
        <span
          style={{
            padding: '0 15px',
            color: color,
            fontSize: '14px',
            whiteSpace: 'nowrap'
          }}
        >
          {children}
        </span>
        <div
          style={{
            flex: 1,
            height: thickness,
            backgroundColor: !dashed && !dotted ? color : 'transparent',
            borderTop:
              dashed || dotted ? `${thickness}px ${dashed ? 'dashed' : 'dotted'} ${color}` : 'none'
          }}
        />
      </div>
    );
  }

  return <div className={`divider ${className}`} style={finalStyle} {...props} />;
};

Divider.propTypes = {
  type: PropTypes.oneOf(['horizontal', 'vertical']),
  thickness: PropTypes.number,
  color: PropTypes.string,
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dashed: PropTypes.bool,
  dotted: PropTypes.bool,
  style: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node
};

export default Divider;
